import React from 'react';
import { Grid, Typography } from '@mui/material';

import { SearchSelectField, SimpleTextField, useSettings, Utils } from 'app/main/components';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

type DetailsSectionBillingProps = {
	module: string;
} & Pick<DetailsSectionInfoContentProps, 'model' | 'isEdit' | 'sendChange'>;

export default function DetailsSectionBilling(props: DetailsSectionBillingProps) {
	const { settings, t } = useSettings(props.module);

	if (!props.isEdit) {
		return (
			<Grid size={12}>
				<Typography className="font-400" variant="subtitle2">
					{props.model[`${settings.modu_short}_bill_comp_title`]}
				</Typography>
				<Typography className="font-400" variant="subtitle2" color="text.secondary">
					{props.model[`${settings.modu_short}_bill_comp_address`] || ''}
				</Typography>
				<Typography className="font-400" variant="subtitle2" color="text.secondary">
					{Utils.formatAddress2({
						t,
						address: {
							zip: props.model[`${settings.modu_short}_bill_comp_zip`],
							city: props.model[`${settings.modu_short}_bill_comp_city`],
							district: props.model[`${settings.modu_short}_bill_comp_dist_iso`],
							country: props.model[`${settings.modu_short}_bill_comp_ctry_title`]
						}
					})}
				</Typography>
			</Grid>
		);
	}

	const regionId = props.model[`${settings.modu_short}_bill_comp_regi_id`];
	const countryId = props.model[`${settings.modu_short}_bill_comp_ctry_id`];

	return (
		<>
			{/* ADDRESS */}
			<Grid size={12}>
				<SimpleTextField
					name={`${settings.modu_short}_bill_comp_address`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS')}
					value={props.model[`${settings.modu_short}_bill_comp_address`]}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* ADDRESS CITY */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name={`${settings.modu_short}_bill_comp_city`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_CITY')}
					value={props.model[`${settings.modu_short}_bill_comp_city`]}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* ADDRESS ZIP */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SimpleTextField
					name={`${settings.modu_short}_bill_comp_zip`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZIP')}
					value={props.model[`${settings.modu_short}_bill_comp_zip`]}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* ADDRESS DISTRICT */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name={`${settings.modu_short}_bill_comp_dist_id`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_DISTRICT')}
					systable="districts"
					value={props.model[`${settings.modu_short}_bill_comp_dist_id`]}
					filters={
						regionId ? [{ filt_field: 'dist_regi_id', filt_clause: 'equals', filt_value: regionId }] : []
					}
					filterOptions={{ stringify: option => `${option.dist_iso} ${option.dist_title}` }}
					getOptionLabel={option => `${option.dist_iso} - ${option.dist_title}`}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					afterChange={value => {
						if (value) {
							props.sendChange(`${settings.modu_short}_bill_comp_ctry_id`, value.regi_ctry_id);
							props.sendChange(`${settings.modu_short}_bill_comp_regi_id`, value.dist_regi_id);
						}
					}}
				/>
			</Grid>
			{/* ADDRESS REGION */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name={`${settings.modu_short}_bill_comp_regi_id`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_REGION')}
					systable="regions"
					value={regionId}
					filters={
						countryId ? [{ filt_field: 'regi_ctry_id', filt_clause: 'equals', filt_value: countryId }] : []
					}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					afterChange={value => {
						if (value) props.sendChange(`${settings.modu_short}_bill_comp_ctry_id`, value.regi_ctry_id);
					}}
				/>
			</Grid>
			{/* ADDRESS COUNTRY */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name={`${settings.modu_short}_bill_comp_ctry_id`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_COUNTRY')}
					systable="countries"
					value={countryId}
					filterOptions={{ stringify: option => `${option.ctry_title} ${option.ctry_iso}` }}
					getOptionLabel={option => `${option.ctry_iso} - ${option.ctry_title}`}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
			{/* ADDRESS ZONE */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name={`${settings.modu_short}_bill_comp_zone_id`}
					label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZONE')}
					systable="zones"
					value={props.model[`${settings.modu_short}_bill_comp_zone_id`]}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
		</>
	);
}
